import * as React from 'react';
import * as ReactDOM from 'react-dom';
import classnames from 'classnames';
import ReactDatePicker, { registerLocale } from 'react-datepicker';

import { Res } from 'core/api';
import { Binding, Result } from 'core/infrastructure';

import { Constraint, check } from './Constraints';
import ErrorBlock from './ErrorBlock';
import { FormComponent } from './Form';
import { formatAsElementId } from './Utils';
import { getAllViewIds } from './ScreenUtils';

import * as locale from 'date-fns/locale';
import { DateUtils } from 'core/utils';

// Registro todos los posibles locales porque no sé a priori cuáles voy
// a necesitar
Object.keys(locale).forEach(key => {
	registerLocale(key, (locale as any)[key]);
});

interface Props<T> extends React.HTMLProps<DatePicker<T>> {
	linkTo: Binding.PropertyPath<T>;
	label?: string;
	boundValue?: Nullable<Date>;
	required?: boolean;
	rules?: Constraint | Constraint[];
	formId?: string;
	dateFormat?: string;
	dateFormatCalendar?: string;
	includeDates?: Date[];
	onValueChanged?: (date: Nullable<Date>) => void;
	minDate?: Date;
}

interface State {
	value: Nullable<Date>;
	errorMsg: string;
}

export class DatePicker<T> extends React.Component<Props<T>, State> implements FormComponent {

	constructor(props: Props<T>) {
		super(props);

		const isNullableDate = (props.boundValue instanceof Date) || props.boundValue === null;
		if (!isNullableDate)
			throw new Error('DatePicker esperaba un bound value de tipo Nullable<Date>');

		this.state = { value: props.boundValue || null, errorMsg: '' };
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<Nullable<Date>> {

		this.setState({ errorMsg: '' });

		const value = this.state.value;
		const rulesError = this.props.rules && check(this.props.rules, value);

		if (this.props.required && !value) {
			this.setState({ errorMsg: Res.Msg.MSG_VALUE_REQUIRED() });
			return Result.error();
		}

		if (rulesError) {
			this.setState({ errorMsg: rulesError });
			return Result.error();
		}

		return Result.ok(value);
	}


	getParentDialogId(): string {
		const allViews = getAllViewIds();
		const parentId = allViews.length ? allViews[allViews.length - 1] : 'app';
		return parentId;
	}

	render() {

		const inputId = formatAsElementId(this.getPath(), this.props.formId || '');

		const onChange: any = (date: Nullable<Date>) => {
			this.setState({ value: date }, () => this.props.onValueChanged && this.props.onValueChanged(date));
		};

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			{
				this.props.label
					? <label className='col-sm-4 control-label' htmlFor={inputId}>
						{this.props.label || ''}
					</label>
					: null
			}

			<div className={classnames({ 'col-sm-8': !!this.props.label, 'col-sm-12': !this.props.label })}>
				<ReactDatePicker id={inputId}

					preventOpenOnFocus={true}
					className='form-control disable-keypad'
					dateFormat={this.props.dateFormat || 'dd/MM/yyyy'}
					dateFormatCalendar={this.props.dateFormatCalendar || 'MMMM yyyy'}
					includeDates={this.props.includeDates}
					minDate={this.props.minDate}
					selected={this.state.value}
					onChange={onChange}
					placeholderText={this.props.placeholder}
					locale={DateUtils.inferLocale()}
					popperContainer={(props) => {
						const parentDialogId = this.getParentDialogId();
						return createPortal({ ...props, parentElementId: parentDialogId });
					}}
					tabIndex={this.props.tabIndex}
					disabled={this.props.disabled} />
				<ErrorBlock msg={this.state.errorMsg} />
			</div>

		</div>;
	}
}


// Necesito crear un contenedor usando un portal para que el popup del calendario
// siempre salga por encima de las cosas. Si no, cuando se muestra en un diálogo
// se provoca que aparezca el scroll en lugar de sacarlo del flujo del diálogo y
// quedar por encima de todo.
//
// Hacer que esto funcione con el focustrap tiene su arte, porque necesito que el contenedor
// quede dentro de la trampa de foco que contiene al DatePicker. Para ello:
// - Si estoy en un diálogo/screen, cuelgo el elemento del div#dialog-XXXX
// - Si no, cuelgo el elemento del div#app


const createPortal: React.FC<React.PropsWithChildren<{ parentElementId: string }>> = (props) => {

	// Los popper se crean al montar el control, algo que solo debería ocurrir si estoy en la vista
	// de primer plano. Si no hay ninguna, asumo que es un control de la pantalla principal.
	const parentId = props.parentElementId;

	const parentEl = document.getElementById(parentId);

	if (!parentEl)
		throw Error('Cannot find parent to create portal');

	const portalClassName = 'react-portal calendar-portal';
	let portalEl: HTMLElement | null = parentEl.querySelector('.react-portal.calendar-portal');
	if (!portalEl) {
		portalEl = document.createElement('div');
		portalEl.className = portalClassName;
		portalEl.style.position = 'absolute';
		portalEl.style.zIndex = '99999';
		portalEl.style.top = '0';
		portalEl.style.left = '0';
		parentEl.appendChild(portalEl);
	}

	return ReactDOM.createPortal(props.children, portalEl);
};

export type DatePickerCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
